import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { getStoreColor } from "../../constants/plasticColors";

interface IMonthsSinceOpeningDataPoint {
  period: string;
  date: string;
  store_name?: string;
  months_since_opening?: number;
  plastic_weight_per_week?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface PlasticMonthsSinceOpeningProps {
  data: IMonthsSinceOpeningDataPoint[] | null;
  isLoading: boolean;
  period: "weekly" | "monthly" | "quarterly" | "yearly";
  from?: string | null;
  to?: string | null;
}

export const PlasticMonthsSinceOpening = React.memo(
  ({ data, isLoading, period, from, to }: PlasticMonthsSinceOpeningProps) => {
    const snapshotRef = useRef(null);
    const [, takeScreenshot] = useScreenshot();

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "months_since_opening.png";
        link.click();
      });
    };

    // Transform data into chart format
    const { categories, series, seriesNames } = useMemo(() => {
      if (!data || data.length === 0) {
        return { categories: [], series: [], seriesNames: [] };
      }

      // Group data by location, using real calendar months on the x-axis
      const groupedData: Record<string, Record<string, number>> = {};
      const allMonthKeys = new Set<string>();

      data.forEach((point) => {
        const key = point.store_name;
        if (!key) return;

        const baseDate = point.month_start || point.date || null;
        if (!baseDate) return;

        // Normalize date to first day of month for sorting/grouping
        const monthKey = dayjs(baseDate).format("YYYY-MM-01");
        allMonthKeys.add(monthKey);

        if (!groupedData[key]) {
          groupedData[key] = {};
        }
        groupedData[key][monthKey] = point.plastic_weight_per_week ?? 0;
      });

      // Sort months (oldest → newest)
      const sortedMonthKeys = Array.from(allMonthKeys).sort((a, b) =>
        dayjs(a).valueOf() - dayjs(b).valueOf()
      );

      // X-axis labels: real months (e.g. "Jan 2024", "Feb 2024", ...)
      const categories = sortedMonthKeys.map((key) =>
        dayjs(key).format("MMM YYYY")
      );

      // Create series for each location
      const seriesNames = Object.keys(groupedData).sort();
      const series = seriesNames.map((name) => ({
        name,
        data: sortedMonthKeys.map(
          (monthKey) => groupedData[name][monthKey] ?? 0
        ),
      }));

      return { categories, series, seriesNames };
    }, [data]);

    // Map colors based on series names
    const colors = useMemo(
      () => seriesNames.map((name) => getStoreColor(name)),
      [seriesNames]
    );

    const options: ApexOptions = useMemo(
      () => ({
        chart: {
          type: "line",
          height: 350,
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        dataLabels: { enabled: false },
        grid: {
          strokeDashArray: 5,
          padding: {
            right: 30,
            left: 10,
          },
        },
        xaxis: {
          title: {
            text: "Months Since Opening",
          },
          categories,
          labels: {
            rotate: -45,
            trim: false,
            style: {
              cssClass: "apexcharts-xaxis-label",
            },
          },
        },
        yaxis: {
          title: {
            text: "Plastic Weight (kg)",
          },
          labels: {
            formatter: (value: number) => {
              const safe =
                typeof value === "number" && !Number.isNaN(value) ? value : 0;
              return safe.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
        },
        colors: colors.length > 0 ? colors : ["#808080"],
        legend: {
          position: "right",
          horizontalAlign: "left",
          markers: { size: 2 },
        },
        markers: {
          size: 4,
          hover: {
            size: 6,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: function ({ series, dataPointIndex, w }: any) {
            const seriesData = series
              .map((seriesValues: any, index: number) => ({
                name: w.globals.seriesNames[index],
                value: seriesValues[dataPointIndex],
                color: w.config.colors[index],
              }))
              .filter(
                (item: any) => item.value !== undefined && item.value !== null
              )
              .sort((a: any, b: any) => (b.value || 0) - (a.value || 0));

            let html =
              '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
              (categories?.[dataPointIndex] ?? "") +
              "</div>";

            seriesData.forEach((item: any) => {
              const raw = item.value;
              const safe =
                typeof raw === "number" && !Number.isNaN(raw) ? raw : 0;
              html += `
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
              <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                <div class="apexcharts-tooltip-y-group">
                  <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                  <span class="apexcharts-tooltip-text-y-value">${safe.toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )} kg</span>
                </div>
              </div>
            </div>`;
            });

            return html;
          },
        },
      }),
      [categories, colors]
    );

    // Format date range for display
    const dateRangeText = useMemo(() => {
      if (!from || !to) return null;
      const fromDate = dayjs(from);
      const toDate = dayjs(to);

      if (period === "monthly") {
        return `${fromDate.format("MMM YYYY")} - ${toDate.format("MMM YYYY")}`;
      } else if (period === "weekly") {
        return `${fromDate.format("MMM DD, YYYY")} - ${toDate.format("MMM DD, YYYY")}`;
      } else if (period === "quarterly") {
        return `${fromDate.format("YYYY")} Q${Math.ceil((fromDate.month() + 1) / 3)}`;
      } else if (period === "yearly") {
        return `${fromDate.format("YYYY")} - ${toDate.format("YYYY")}`;
      }
      return null;
    }, [from, to, period]);

    if (isLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div ref={snapshotRef} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                Plastic Weight Since Opening
              </h2>
              <Tooltip
                content="Non-cumulative: Shows plastic weight since opening for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            {dateRangeText && (
              <p className="text-sm text-gray-500 mt-1">{dateRangeText}</p>
            )}
          </div>
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
        <div>
          <Chart options={options} series={series} type="line" height={350} />
        </div>
      </div>
    );
  }
);

PlasticMonthsSinceOpening.displayName = "PlasticMonthsSinceOpening";
